import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import API from '../utils/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Status, StatusColor, Color, Role, ViewMode } from '../constants';

const STATUS_ORDER = [
  Status.PENDING, Status.ASSIGNED, Status.COMPLETED,
  Status.NOT_COMPLETED, Status.CANCELLED,
];

export default function Dashboard() {
  const { user, isDelegated } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(ViewMode.DEFAULT);

  // Only a delegated ASM still switches views — DSMs always see their whole
  // warehouse, so their toggle was removed.
  const showToggle = user?.role === Role.ASM && isDelegated();

  const toggleOptions = [
    { value: ViewMode.DEFAULT, label: 'My Route' },
    { value: ViewMode.TEAM,    label: 'DSM Team' },
  ];

  useEffect(() => {
    setLoading(true);
    API.get('/work-orders/dashboard', { params: { view_mode: viewMode } })
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [viewMode]);

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-gray-400">
      Loading dashboard...
    </div>
  );
  if (!data) return null;

  const pieData = STATUS_ORDER
    .map(s => ({ name: s, value: data.status_counts[s] || 0,
                 color: StatusColor[s]?.dot }))
    .filter(d => d.value > 0);

  const subtitle = {
    [Role.SUPER_ADMIN]: 'All regions',
    [Role.ADMIN]:       'All regions',
    [Role.GSM]:         'All regions',
    [Role.DSM]:         'Your warehouse',
    [Role.ASM]:         'Your route',
    [Role.SA]:          'All regions',
    [Role.ST]:          'Orders assigned to you',
  }[user?.role] || '';

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-500 text-sm mt-1">{subtitle}</p>
        </div>

        {/* View toggle */}
        {showToggle && (
          <div className="flex rounded-lg border border-gray-200 overflow-hidden flex-shrink-0">
            {toggleOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setViewMode(opt.value)}
                className="px-3 py-1.5 text-sm font-medium transition-colors"
                style={viewMode === opt.value
                  ? { backgroundColor: Color.NAVY, color: '#fff' }
                  : { backgroundColor: '#fff', color: '#374151' }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Empty state */}
      {data.total === 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-8 text-center mb-6">
          <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-7 h-7 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-gray-700 mb-1">No work orders yet</h2>
          <p className="text-gray-500 text-sm">Nothing to show for this view.</p>
        </div>
      )}

      {data.total > 0 && (
        <>
          {/* Stat cards */}
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
            <StatCard
              label="Total"
              value={data.total}
              bg="#E8EAF6"
              color={Color.NAVY}
              onClick={() => navigate('/work-orders')}
            />
            {STATUS_ORDER.map(s => {
              const count = data.status_counts[s] || 0;
              if (count === 0 && s === Status.NOT_COMPLETED) return null;
              if (count === 0 && s === Status.CANCELLED) return null;
              const sc = StatusColor[s];
              return (
                <StatCard
                  key={s}
                  label={s}
                  value={count}
                  bg={sc.bg}
                  color={sc.text}
                  onClick={() => navigate(`/work-orders?status=${encodeURIComponent(s)}`)}
                />
              );
            })}
            {data.overdue > 0 && (
              <StatCard
                label="Overdue"
                value={data.overdue}
                bg="#FDECEA"
                color={Color.RED}
                onClick={() => navigate('/work-orders?overdue=1')}
              />
            )}
          </div>

          {/* Charts row 1 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">

            {/* Pie — by status */}
            <ChartCard title="Orders by Status">
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%" cy="50%"
                    innerRadius={55} outerRadius={95}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(v, n) => [v, n]} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* Bar — by category */}
            <ChartCard title="Orders by Category">
              {data.by_category.length === 0
                ? <Empty />
                : (
                  <ResponsiveContainer width="100%" height={240}>
                    <BarChart
                      data={data.by_category}
                      margin={{ top: 5, right: 10, left: -20, bottom: 60 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }}
                        angle={-35} textAnchor="end" interval={0} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="count" fill={Color.LIGHT_BLUE}
                        radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
            </ChartCard>
          </div>

          {/* Charts row 2 */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

            {/* Bar — by route (not for ASM/SA/ST) */}
            {![Role.ASM, Role.SA, Role.ST].includes(user?.role) &&
              data.by_route.length > 0 && (
              <ChartCard title="Orders by Route">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={data.by_route}
                    margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill={Color.NAVY}
                      radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}

            {/* Bar — completed by ST (ASM, DSM, GSM) */}
            {[Role.ASM, Role.DSM, Role.GSM, Role.SUPER_ADMIN, Role.ADMIN].includes(user?.role) &&
              data.by_assignee.length > 0 && (
              <ChartCard title="Completed by Sales Trainee">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={data.by_assignee}
                    margin={{ top: 5, right: 10, left: -20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="count" fill={Color.YELLOW}
                      radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </ChartCard>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, bg, color, onClick }) {
  return (
    <div
      onClick={onClick}
      className="rounded-xl p-3 cursor-pointer hover:shadow-md transition-shadow"
      style={{ backgroundColor: bg, color }}
    >
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs font-medium mt-0.5 leading-tight">{label}</p>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <h2 className="font-semibold text-gray-700 mb-4 text-sm">{title}</h2>
      {children}
    </div>
  );
}

function Empty() {
  return (
    <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
      No data yet
    </div>
  );
}
